import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  LogOut,
  User as UserIcon,
  Loader2,
  Plug,
  CheckCircle2,
  XCircle,
  Database,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react';
import Login from './Login';
import CopilotPanel from './AiCopilot/CopilotPanel';
import AskDataPanel from './AiCopilot/AskDataPanel';
import SqlStudioPanel from './AiCopilot/SqlStudioPanel';
import Connections from './Connections';
import UserLookup from './UserLookup';
import UserAccessReview from './UserAccessReview';
import AuditThreatDashboard from './AuditThreatDashboard';
import HdlErrorsDashboard from './HdlErrorsDashboard';
import SodRulesPage from './SodRulesPage';
import ReportsCatalog from './Reports/ReportsCatalog';
import { getApiBase } from './apiBase';
import { getProduct, getAllViews, isViewAllowed } from './productConfig';
import OrcaLogo from './OrcaLogo';
import ThemeToggle from './ThemeToggle';
import MarketingApp, { isMarketingPath, usePath } from './marketing/MarketingApp';

const API = axios.create({
  baseURL: getApiBase(),
  withCredentials: true,
  timeout: 60000,
});

const PRODUCT = getProduct();
const COPILOT_VIEWS = PRODUCT.copilotViews;
const ADMIN_VIEWS = PRODUCT.adminViews;
const VIEWS = getAllViews(PRODUCT);

const SIDEBAR_COLLAPSED_KEY = 'orcaAi.sidebar.collapsed';

function readSidebarCollapsed() {
  try {
    return sessionStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
}

function ConnectionBadge({ connection, testResult, testing, onTest, onManage }) {
  const configured = !!connection?.isConfigured;
  const tested = testResult != null;
  const ok = tested && testResult.success;
  const failed = tested && !testResult.success;
  const Icon = !configured || failed ? XCircle : ok ? CheckCircle2 : Plug;
  const tone = !configured || failed
    ? 'text-rose-400'
    : ok
    ? 'text-emerald-500'
    : 'text-orca-accent';

  const label =
    configured &&
    (connection.source === 'db'
      ? connection.name
      : connection.description || connection.baseUrl.replace(/^https?:\/\//, ''));

  return (
    <div className="rounded-lg border border-orca bg-orca-panel p-2.5 text-xs text-orca-muted">
      <div className="mb-1.5 flex items-center gap-2">
        <Icon className={`h-3.5 w-3.5 ${tone}`} />
        <span className="text-[10px] font-semibold uppercase tracking-wide text-orca-muted">
          HCM Connection
        </span>
        {configured && connection.source === 'db' && (
          <span className="ml-auto rounded-full bg-emerald-900/60 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-emerald-200">
            saved
          </span>
        )}
        {configured && connection.source === 'env' && (
          <span className="ml-auto rounded-full border border-orca bg-orca-raised px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-orca-muted">
            .env
          </span>
        )}
      </div>
      {configured ? (
        <>
          <div className="truncate font-medium text-orca-strong" title={connection.baseUrl}>
            {label}
          </div>
          <div className="truncate text-orca-muted">user: {connection.username}</div>
        </>
      ) : (
        <div className="leading-relaxed text-orca-muted">
          Not configured — add a connection or set ORACLE_HCM_URL in .env
        </div>
      )}
      <div className="mt-2 flex gap-1.5">
        <button
          type="button"
          onClick={onTest}
          disabled={testing || !configured}
          className="flex flex-1 items-center justify-center gap-1.5 rounded-md bg-orca-accent orca-on-accent px-2 py-1.5 text-[11px] font-medium hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {testing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plug className="h-3 w-3" />}
          {testing ? 'Testing…' : tested ? 'Re-test' : 'Test'}
        </button>
        <button
          type="button"
          onClick={onManage}
          className="flex items-center justify-center gap-1 rounded-md border border-orca bg-orca-raised px-2 py-1.5 text-[11px] font-medium text-orca-strong hover:brightness-110"
          title="Manage saved connections"
        >
          Manage
        </button>
      </div>
      {tested && (
        <div
          className={`mt-1.5 break-words rounded px-2 py-1 text-[11px] ${
            ok ? 'bg-emerald-900/40 text-emerald-200' : 'bg-rose-900/40 text-rose-200'
          }`}
        >
          {ok ? `OK · ${testResult.latencyMs} ms` : testResult.message}
          {ok && testResult.sample?.PersonNumber && (
            <div className="text-emerald-300/80">
              sample worker: {testResult.sample.PersonNumber}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function hasView(viewId) {
  return VIEWS.some((v) => v.id === viewId);
}

export default function App() {
  const [path, navigate] = usePath();
  const [user, setUser] = useState(null);
  const [view, setView] = useState(PRODUCT.defaultView);
  const [bootLoading, setBootLoading] = useState(true);
  const [health, setHealth] = useState(null);
  const [hcmConn, setHcmConn] = useState(null);
  const [hcmTest, setHcmTest] = useState(null);
  const [hcmTesting, setHcmTesting] = useState(false);
  const [copilotHandoff, setCopilotHandoff] = useState(null);
  const [sqlStudioInitial, setSqlStudioInitial] = useState(null);
  const [userLookupFocusUsername, setUserLookupFocusUsername] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readSidebarCollapsed);

  useEffect(() => {
    try {
      sessionStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(sidebarCollapsed));
    } catch {
      // ignore quota / private mode
    }
  }, [sidebarCollapsed]);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const v = params.get('view');
      const u = params.get('user');
      if (v && isViewAllowed(v, PRODUCT)) setView(v);
      if (u && hasView('users')) setUserLookupFocusUsername(u.trim());
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    API.get('/auth/me', { validateStatus: (s) => s === 200 || s === 401 })
      .then((res) => setUser(res.status === 200 ? res.data.user : null))
      .catch(() => setUser(null))
      .finally(() => setBootLoading(false));
  }, []);

  useEffect(() => {
    if (user && path === '/login') navigate('/', { replace: true });
  }, [user, path, navigate]);

  useEffect(() => {
    if (!user) {
      setHealth(null);
      setHcmConn(null);
      setHcmTest(null);
      return;
    }
    API.get('/api/ai/health', { validateStatus: () => true })
      .then((res) => {
        if (res.status === 200) setHealth(res.data);
        else setHealth(null);
      })
      .catch(() => setHealth(null));
    API.get('/api/hcm/connection', { validateStatus: () => true })
      .then((res) => setHcmConn(res.status === 200 ? res.data : null))
      .catch(() => setHcmConn(null));
  }, [user]);

  async function runHcmTest() {
    setHcmTesting(true);
    setHcmTest(null);
    try {
      const res = await API.post('/api/hcm/test', {}, { validateStatus: () => true });
      setHcmTest(res.data);
    } catch (err) {
      setHcmTest({ success: false, message: err.message || 'Test failed' });
    } finally {
      setHcmTesting(false);
    }
  }

  function refreshActiveConnection() {
    setHcmTest(null);
    API.get('/api/hcm/connection', { validateStatus: () => true })
      .then((res) => setHcmConn(res.status === 200 ? res.data : null))
      .catch(() => setHcmConn(null));
  }

  function handleCopilotHandoff(payload) {
    const target = payload.feature || 'security';
    if (!hasView(target)) return;
    setCopilotHandoff(payload);
    setView(target);
  }

  function handleOpenUserLookup(focusUsername = '') {
    if (!hasView('users')) return;
    setUserLookupFocusUsername(typeof focusUsername === 'string' ? focusUsername : '');
    setView('users');
  }

  async function logout() {
    try {
      const res = await API.post('/auth/logout');
      if (res.data?.redirectUrl) {
        window.location.href = res.data.redirectUrl;
        return;
      }
    } catch (_) {
      // ignore
    }
    setUser(null);
  }

  if (bootLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-orca-base text-orca-muted">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Loading…
      </div>
    );
  }

  const wantApp = path === '/' || path === '/app';

  if (!user) {
    if (path === '/login' || path === '/app') {
      return (
        <Login
          product={PRODUCT}
          onSuccess={(nextUser) => {
            setUser(nextUser);
            navigate('/', { replace: true });
          }}
          onHome={() => navigate('/')}
        />
      );
    }
    return <MarketingApp path={path} navigate={navigate} onSignIn={() => navigate('/login')} />;
  }

  if (isMarketingPath(path) && !wantApp) {
    return (
      <MarketingApp
        path={path}
        navigate={navigate}
        signedIn
        onSignIn={() => navigate('/')}
        onOpenApp={() => navigate('/')}
      />
    );
  }

  const navBtnClass = (active) =>
    `flex w-full items-center rounded-lg text-sm font-medium transition-colors ${
      sidebarCollapsed ? 'justify-center px-2 py-2.5' : 'gap-2.5 px-3 py-2.5 text-left'
    } ${
      active
        ? 'bg-orca-accent orca-on-accent'
        : 'text-orca-muted hover:bg-orca-raised hover:text-orca-strong'
    }`;

  const copilotDisabledBanner = (
    <div className="rounded-xl border border-amber-700/60 bg-amber-950/50 p-4 text-sm text-amber-200">
      The AI Copilot is not enabled for this user. Ask an admin to add you to
      <code className="mx-1 rounded bg-amber-900/60 px-1.5 py-0.5">AI_COPILOT_ALLOWED_USERS</code>
      on the server.
    </div>
  );

  return (
    <div className="orca-app min-h-screen flex bg-orca-base text-orca-strong">
      <aside
        className={`hidden md:flex shrink-0 flex-col bg-orca-panel border-r border-orca text-orca-strong py-4 transition-[width] duration-200 ease-out ${
          sidebarCollapsed ? 'w-14 px-1.5' : 'w-64 px-3'
        }`}
      >
        <div
          className={`mb-4 flex items-center ${
            sidebarCollapsed ? 'flex-col gap-2 px-0.5' : 'gap-2 px-2'
          }`}
        >
          <OrcaLogo className="h-9 w-9" />
          {!sidebarCollapsed && (
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-orca-strong">{PRODUCT.name}</div>
              <div className="truncate text-xs text-orca-muted">{PRODUCT.tagline}</div>
            </div>
          )}
          <button
            type="button"
            onClick={() => setSidebarCollapsed((c) => !c)}
            className={`shrink-0 rounded-lg p-1.5 text-orca-muted transition-colors hover:bg-orca-raised hover:text-orca-strong ${
              sidebarCollapsed ? '' : 'ml-auto'
            }`}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!sidebarCollapsed}
          >
            {sidebarCollapsed ? (
              <PanelLeftOpen className="h-4 w-4" />
            ) : (
              <PanelLeftClose className="h-4 w-4" />
            )}
          </button>
        </div>

        {!sidebarCollapsed && (
          <p className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-orca-muted">
            Copilots
          </p>
        )}
        {sidebarCollapsed && <div className="my-1 border-t border-orca" aria-hidden />}
        <nav className="space-y-1">
          {COPILOT_VIEWS.map((v) => {
            const Icon = v.icon;
            const active = view === v.id;
            return (
              <button
                key={v.id}
                type="button"
                onClick={() => setView(v.id)}
                className={navBtnClass(active)}
                title={sidebarCollapsed ? v.label : undefined}
              >
                <Icon className={`h-4 w-4 shrink-0 ${active ? '' : 'text-orca-accent'}`} />
                {!sidebarCollapsed && v.label}
              </button>
            );
          })}
        </nav>

        {!sidebarCollapsed && (
          <p className="mt-4 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-orca-muted">
            Admin
          </p>
        )}
        {sidebarCollapsed && <div className="my-2 border-t border-orca" aria-hidden />}
        <nav className="space-y-1">
          {ADMIN_VIEWS.map((v) => {
            const Icon = v.icon;
            const active = view === v.id;
            return (
              <button
                key={v.id}
                type="button"
                onClick={() => setView(v.id)}
                className={navBtnClass(active)}
                title={sidebarCollapsed ? v.label : undefined}
              >
                <Icon className={`h-4 w-4 shrink-0 ${active ? '' : 'text-orca-accent'}`} />
                {!sidebarCollapsed && v.label}
              </button>
            );
          })}
        </nav>

        <div
          className={`mt-auto space-y-3 border-t border-orca ${
            sidebarCollapsed ? 'pt-3' : 'pt-4'
          }`}
        >
          {!sidebarCollapsed ? (
            <ConnectionBadge
              connection={hcmConn}
              testResult={hcmTest}
              testing={hcmTesting}
              onTest={runHcmTest}
              onManage={() => setView('connections')}
            />
          ) : (
            <button
              type="button"
              onClick={() => setView('connections')}
              className="flex w-full justify-center rounded-lg p-2 text-orca-muted hover:bg-orca-raised hover:text-orca-strong"
              title="Connections"
            >
              <Database className="h-4 w-4 text-orca-accent" />
            </button>
          )}
          <ThemeToggle collapsed={sidebarCollapsed} />
          <div
            className={`flex items-center text-xs text-orca-muted ${
              sidebarCollapsed ? 'justify-center px-0 py-1' : 'gap-2 px-3 py-2'
            }`}
            title={sidebarCollapsed ? user.username : undefined}
          >
            <UserIcon className="h-3.5 w-3.5 shrink-0" />
            {!sidebarCollapsed && <span className="truncate">{user.username}</span>}
          </div>
          <button
            type="button"
            onClick={logout}
            className={navBtnClass(false)}
            title={sidebarCollapsed ? 'Sign out' : undefined}
          >
            <LogOut className="h-4 w-4 shrink-0 text-orca-accent" />
            {!sidebarCollapsed && 'Sign out'}
          </button>
        </div>
      </aside>

      <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-orca-base p-2 md:p-3">
        <div className="md:hidden mb-4 flex items-center justify-between gap-2 rounded-xl bg-orca-panel border border-orca px-3 py-2 text-orca-strong">
          <div className="flex min-w-0 items-center gap-2">
            <OrcaLogo className="h-6 w-6 shrink-0 rounded-md" />
            <div className="min-w-0">
              <span className="text-sm font-semibold">{PRODUCT.name}</span>
              <span className="ml-1 text-xs text-orca-muted">{PRODUCT.tagline}</span>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <ThemeToggle collapsed compact />
            <select
            value={view}
            onChange={(e) => setView(e.target.value)}
            className="rounded-lg bg-orca-raised px-2 py-1 text-xs text-orca-strong border border-orca"
          >
            {VIEWS.map((v) => (
              <option key={v.id} value={v.id}>{v.label}</option>
            ))}
          </select>
          </div>
        </div>

        <div className="orca-theme flex min-h-0 flex-1 flex-col overflow-hidden">
          {hasView('connections') && (
            <div className={view === 'connections' ? '' : 'hidden'} aria-hidden={view !== 'connections'}>
              <Connections onActiveChanged={refreshActiveConnection} />
            </div>
          )}
          {hasView('users') && (
            <div className={view === 'users' ? '' : 'hidden'} aria-hidden={view !== 'users'}>
              <UserLookup
                onAskCopilot={handleCopilotHandoff}
                focusUsername={userLookupFocusUsername}
                onFocusUsernameConsumed={() => setUserLookupFocusUsername('')}
              />
            </div>
          )}
          {hasView('uar') && (
            <div className={view === 'uar' ? '' : 'hidden'} aria-hidden={view !== 'uar'}>
              <UserAccessReview />
            </div>
          )}
          {hasView('audit-alerts') && (
            <div className={view === 'audit-alerts' ? '' : 'hidden'} aria-hidden={view !== 'audit-alerts'}>
              <AuditThreatDashboard />
            </div>
          )}
          {hasView('hdl-errors') && (
            <div className={view === 'hdl-errors' ? '' : 'hidden'} aria-hidden={view !== 'hdl-errors'}>
              <HdlErrorsDashboard />
            </div>
          )}
          {hasView('sod-rules') && (
            <div className={view === 'sod-rules' ? '' : 'hidden'} aria-hidden={view !== 'sod-rules'}>
              <SodRulesPage />
            </div>
          )}
          {hasView('reports-catalog') && (
            <div className={view === 'reports-catalog' ? '' : 'hidden'} aria-hidden={view !== 'reports-catalog'}>
              <ReportsCatalog onAskCopilot={handleCopilotHandoff} />
            </div>
          )}
          {hasView('ask-data') && (
            <div className={view === 'ask-data' ? 'flex h-full min-h-0 flex-1 flex-col' : 'hidden'} aria-hidden={view !== 'ask-data'}>
              <AskDataPanel
                title="Ask Data"
                standalone
                askDataProvider={health?.askData}
              />
            </div>
          )}
          {hasView('sql-studio') && (
            <div
              className={view === 'sql-studio' ? 'flex h-full min-h-0 flex-1 flex-col' : 'hidden'}
              aria-hidden={view !== 'sql-studio'}
            >
              <SqlStudioPanel
                title={COPILOT_VIEWS.find((v) => v.id === 'sql-studio')?.label}
                initialQuestion={sqlStudioInitial}
                onConsumeInitial={() => setSqlStudioInitial(null)}
                onOpenUserLookup={handleOpenUserLookup}
                connectionKey={
                  hcmConn?.isConfigured
                    ? `${hcmConn.source}:${hcmConn.id || hcmConn.baseUrl || ''}`
                    : ''
                }
              />
            </div>
          )}
          {['report', 'bpm', 'security'].filter(hasView).map((featureId) => {
            const meta = COPILOT_VIEWS.find((v) => v.id === featureId);
            return (
              <div
                key={featureId}
                className={view === featureId ? 'flex h-full min-h-0 flex-1 flex-col' : 'hidden'}
                aria-hidden={view !== featureId}
              >
                {health && !health.enabled ? (
                  copilotDisabledBanner
                ) : (
                  <CopilotPanel
                    feature={featureId}
                    title={meta?.label}
                    bpmLayout={featureId === 'bpm' ? 'landing' : undefined}
                    onOpenApprovalDesigner={
                      featureId === 'bpm' && hasView('approval-designer')
                        ? () => setView('approval-designer')
                        : undefined
                    }
                    onOpenConnections={
                      featureId === 'bpm' && hasView('connections')
                        ? () => setView('connections')
                        : undefined
                    }
                    onHandoffToCopilot={
                      featureId === 'bpm'
                        ? (payload) => {
                            setCopilotHandoff(payload);
                            setView('bpm');
                          }
                        : undefined
                    }
                    handoff={copilotHandoff?.feature === featureId ? copilotHandoff : null}
                    onHandoffConsumed={() => setCopilotHandoff(null)}
                  />
                )}
              </div>
            );
          })}
          {hasView('approval-designer') && (
            <div
              className={view === 'approval-designer' ? 'flex h-full min-h-0 flex-1 flex-col' : 'hidden'}
              aria-hidden={view !== 'approval-designer'}
            >
              {health && !health.enabled ? (
                copilotDisabledBanner
              ) : (
                <CopilotPanel
                  feature="bpm"
                  bpmLayout="designer"
                  title={COPILOT_VIEWS.find((v) => v.id === 'approval-designer')?.label}
                  handoff={
                    copilotHandoff?.feature === 'bpm' || copilotHandoff?.feature === 'approval-designer'
                      ? copilotHandoff
                      : null
                  }
                  onHandoffConsumed={() => setCopilotHandoff(null)}
                  onHandoffToCopilot={(payload) => {
                    setCopilotHandoff(payload);
                    setView('bpm');
                  }}
                />
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
